import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Settings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/risk/settings").then((r) => setS(r.data)); }, []);

  const update = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/risk/settings", {
        daily_buy_cap: Number(s.daily_buy_cap),
        per_trade_max: Number(s.per_trade_max),
        max_open_positions: Number(s.max_open_positions),
        default_stop_loss_pct: Number(s.default_stop_loss_pct),
        default_take_profit_pct: Number(s.default_take_profit_pct),
        trading_start: s.trading_start,
        trading_end: s.trading_end,
      });
      setS(r.data);
      toast.success("Risk settings saved");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setBusy(false);
    }
  };

  if (!s) return <Layout><div className="p-6 text-xs text-[#4B5563]">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="p-6 max-w-3xl">
        <header className="mb-6">
          <h1 className="text-2xl font-medium tracking-tight">Risk Control Center</h1>
          <p className="text-xs text-[#8B95A5] mt-1">
            Hard guardrails. Every order — manual, AI-suggested, or auto-fired — must clear these checks.
          </p>
        </header>

        <div className="panel p-6 space-y-6" data-testid="risk-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Daily Buy Cap (USD)" testId="risk-daily-buy-cap">
              <input type="number" className="field" value={s.daily_buy_cap} onChange={(e) => update("daily_buy_cap", e.target.value)} />
              <Hint>Max total notional you can buy in a 24-hour window.</Hint>
            </Field>
            <Field label="Per-Trade Max (USD)" testId="risk-per-trade-max">
              <input type="number" className="field" value={s.per_trade_max} onChange={(e) => update("per_trade_max", e.target.value)} />
              <Hint>Max notional per single order.</Hint>
            </Field>
            <Field label="Max Open Positions" testId="risk-max-positions">
              <input type="number" className="field" value={s.max_open_positions} onChange={(e) => update("max_open_positions", e.target.value)} />
              <Hint>Stops new buys once this count is reached.</Hint>
            </Field>
            <Field label="Default Stop-Loss %" testId="risk-default-sl">
              <input type="number" step="0.1" className="field" value={s.default_stop_loss_pct} onChange={(e) => update("default_stop_loss_pct", e.target.value)} />
            </Field>
            <Field label="Default Take-Profit %" testId="risk-default-tp">
              <input type="number" step="0.1" className="field" value={s.default_take_profit_pct} onChange={(e) => update("default_take_profit_pct", e.target.value)} />
            </Field>
            <Field label="Trading Window (UTC)">
              <div className="flex gap-2">
                <input type="time" className="field" value={s.trading_start} onChange={(e) => update("trading_start", e.target.value)} data-testid="risk-trading-start" />
                <span className="text-[#4B5563] flex items-center text-xs">→</span>
                <input type="time" className="field" value={s.trading_end} onChange={(e) => update("trading_end", e.target.value)} data-testid="risk-trading-end" />
              </div>
              <Hint>Orders outside this window are blocked. Use 00:00 → 23:59 to trade 24/7 (crypto).</Hint>
            </Field>
          </div>

          <div className="border-t border-[#272A30] pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#8B95A5]">Execution Mode</div>
                <div className="text-sm mt-1">Currently: <span className={s.execution_mode === "auto" ? "text-[#00E5FF]" : "text-[#FFB300]"}>{s.execution_mode.toUpperCase()}</span></div>
                <div className="text-[10px] text-[#4B5563] mt-1">Use the cockpit toggle to switch modes.</div>
              </div>
              <button className="btn-primary" disabled={busy} onClick={save} data-testid="risk-save-btn">
                {busy ? "Saving…" : "Save settings"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, testId, children }) {
  return (
    <div data-testid={testId}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

function Hint({ children }) {
  return <div className="text-[10px] text-[#4B5563] mt-1">{children}</div>;
}
